'use client';

import { useRef, type ReactElement } from 'react';
import { imgOpt } from '@fshr/shared';
import { cn, getConfig } from '@fshr/shared-react';
import AmbientVideo from '../ambient-video';
import Image from '../image';
import getCtaStyles from '../button/get-cta-styles';
import BevSafeText from '../bev-safe-text';
import { resolveHeadingLevel } from '../rich-text-title';
import type { ImageRoughSizeOptions } from '../image/optimize-image-size';
import type { AsymmetricalCardData, CardVariant } from './types';
import { useTranslations } from '@fshr/context/i18n-context';

type Props = {
	card: AsymmetricalCardData;
	variant: CardVariant;
	titleClamp?: 2 | 3;
	className?: string;
	widthClassName?: string;
	aspectClassName?: string;
	/**
	 * How much of the container the card media fills. Defaults to the multi-card
	 * carousel widths (landscape ≈ half, portrait ≈ a quarter); the carousel passes
	 * `l` when a single card spans the full row.
	 */
	roughSize?: ImageRoughSizeOptions;
	onReserve?: (card: AsymmetricalCardData) => void;
};

export default function AsymmetricalCard({
	card,
	variant,
	titleClamp = 2,
	className,
	widthClassName,
	aspectClassName,
	roughSize,
	onReserve,
}: Props): ReactElement {
	const t = useTranslations();
	const videoRef = useRef<HTMLVideoElement>(null);
	const isLandscape = variant === 'landscape';
	const mediaRoughSize: ImageRoughSizeOptions = roughSize ?? (isLandscape ? 'm' : 's');
	const cardWidthClass =
		widthClassName ?? (isLandscape ? 'w-[315px] md:w-[615px]' : 'w-[315px] md:w-[292px]');
	const mediaAspectClass =
		aspectClassName ??
		(isLandscape ? 'aspect-[315/394] md:aspect-[615/366]' : 'aspect-[315/394] md:aspect-[292/366]');
	const TitleTag = resolveHeadingLevel(card.headingLevel, 'h3');
	const hasWidgetReserve = Boolean(card.reserve && onReserve);
	const hasReserve = hasWidgetReserve || Boolean(card.reserveHref);
	const showCta = hasReserve;

	const { NEXT_PUBLIC_IMAGE_BASE_URL } = getConfig();
	const base = `${NEXT_PUBLIC_IMAGE_BASE_URL || ''}`;

	const desktopImage = isLandscape ? card.image : (card.portraitImage ?? card.image);
	const mobileImage = isLandscape ? (card.portraitImage ?? card.mobileImage) : undefined;
	const posterSrc = desktopImage?.defaultImage?.path
		? `${base}${imgOpt({ path: desktopImage.defaultImage.path, cropping: desktopImage.defaultImage.cropping })}`
		: undefined;

	const playVideo = () => {
		const video = videoRef.current;
		if (!video) return;
		if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
		void video.play().catch(() => undefined);
	};

	const pauseVideo = () => {
		const video = videoRef.current;
		if (!video) return;
		video.pause();
		video.currentTime = 0;
	};

	return (
		<article
			className={cn(
				'group/card relative flex shrink-0 snap-start flex-col gap-y-stack-m md:gap-y-stack-s',
				cardWidthClass,
				className
			)}
			onMouseEnter={playVideo}
			onMouseLeave={pauseVideo}
		>
			<div className={cn('relative w-full overflow-hidden', mediaAspectClass)}>
				<Image
					roughSize={mediaRoughSize}
					imagePrimitive={desktopImage}
					className={cn(
						'absolute inset-0 h-full w-full object-cover object-center transition-transform duration-500 ease-out motion-reduce:transition-none group-hover/card:scale-110',
						mobileImage && 'hidden md:block'
					)}
				/>
				{mobileImage && (
					<Image
						roughSize={mediaRoughSize}
						imagePrimitive={mobileImage}
						className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-500 ease-out motion-reduce:transition-none group-hover/card:scale-110 md:hidden"
					/>
				)}

				{card.videoUrl && (
					<AmbientVideo
						ref={videoRef}
						desktop={{ videoId: card.videoUrl, codecs: {}, src: card.videoUrl }}
						poster={posterSrc}
						autoplay={false}
						showToggle={false}
						preload="none"
						className="opacity-0 transition-opacity duration-300 group-hover/card:opacity-100"
					/>
				)}

				{card.badge && (
					<span className="absolute left-060 top-060 bg-surface-default px-tag-padding-x py-tag-padding-y text-label uppercase text-text-primary backdrop-blur-[15px]">
						{card.badge}
					</span>
				)}
			</div>

			<div className="flex flex-col gap-y-stack-s">
				<div className="flex flex-col gap-y-stack-xs">
					{card.eyebrow && (
						<p
							className={`line-clamp-2 uppercase text-text-secondary ${
								isLandscape ? 'text-label' : 'text-overline'
							}`}
						>
							<BevSafeText>{card.eyebrow}</BevSafeText>
						</p>
					)}

					<div className="flex flex-col gap-y-stack-2xs">
						<TitleTag
							className={`text-h3 pb-[0.15em] text-text-heading ${
								titleClamp === 3 ? 'line-clamp-3' : 'line-clamp-2'
							}`}
						>
							{card.href ? (
								<a
									href={card.href}
									target={card.newWindow ? '_blank' : undefined}
									rel={card.newWindow ? 'noopener' : undefined}
									className="before:absolute before:inset-0 before:z-0 focus-visible:outline-2 focus-visible:outline-offset-2"
								>
									<BevSafeText>{card.title}</BevSafeText>
								</a>
							) : (
								<BevSafeText>{card.title}</BevSafeText>
							)}
						</TitleTag>

						{card.award && (
							<p className="text-caption line-clamp-2 text-text-secondary">{card.award}</p>
						)}
					</div>
				</div>

				{card.description && (
					<p className="text-body2 line-clamp-2 text-text-primary">
						<BevSafeText>{card.description}</BevSafeText>
					</p>
				)}

				{showCta && (
					<div className="flex items-center">
						{hasWidgetReserve ? (
							<button
								type="button"
								onClick={() => onReserve?.(card)}
								className={cn('relative z-10', getCtaStyles({ intent: 'outline', size: 'small' }))}
							>
								{card.reserveLabel ?? t('Reserve')}
							</button>
						) : (
							hasReserve && (
								<a
									href={card.reserveHref}
									className={cn(
										'relative z-10',
										getCtaStyles({ intent: 'outline', size: 'small' })
									)}
								>
									{card.reserveLabel ?? t('Reserve')}
								</a>
							)
						)}
					</div>
				)}
			</div>
		</article>
	);
}
